import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Checkbox,
  Tabs,
  Tab,
  Switch,
  Chip,
  Tooltip,
  Divider,
  Fade,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import PeopleIcon from "@material-ui/icons/People";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import PersonIcon from "@material-ui/icons/Person";
import InfoIcon from "@material-ui/icons/Info";
import LinkIcon from "@material-ui/icons/Link";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import SettingsIcon from "@material-ui/icons/Settings";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import SaveIcon from "@material-ui/icons/Save";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 780,
    margin: "0 auto",
  },
  backButton: {
    marginRight: theme.spacing(1),
    textTransform: "none",
    fontWeight: 500,
  },
  paper: {
    borderRadius: 16,
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  tabs: {
    backgroundColor: theme.palette.background.default,
    borderBottom: `2px solid ${theme.palette.divider}`,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 600,
      fontSize: 14,
      minHeight: 56,
      "&.Mui-selected": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: "3px 3px 0 0",
    },
  },
  tabPanel: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 15,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2.5),
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "&::before": {
      content: '""',
      width: 4,
      height: 22,
      backgroundColor: theme.palette.primary.main,
      borderRadius: 2,
      flexShrink: 0,
    },
  },
  sectionTitleIcon: {
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  field: {
    marginBottom: theme.spacing(2.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.light,
      },
    },
  },
  coverSection: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
  },
  coverPlaceholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    boxSizing: "border-box",
    border: "2px dashed",
    borderColor: theme.palette.grey[300],
    borderRadius: 14,
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: theme.palette.grey[50],
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main + "0A",
      transform: "translateY(-1px)",
    },
  },
  coverPreview: {
    width: 140,
    height: 140,
    objectFit: "cover",
    borderRadius: "50%",
    display: "block",
    border: `3px solid ${theme.palette.primary.light}`,
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  },
  coverCurrent: {
    width: 140,
    height: 140,
    objectFit: "cover",
    borderRadius: "50%",
    display: "block",
    border: `3px solid ${theme.palette.grey[300]}`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  coverActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  inputFile: {
    display: "none",
  },
  submitButton: {
    marginTop: theme.spacing(3),
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 15,
    padding: theme.spacing(1.2, 4),
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    "&:hover": {
      boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
    },
  },
  addParticipantRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    flexWrap: "wrap",
    alignItems: "center",
  },
  addInput: {
    flex: 1,
    maxWidth: 280,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
    },
  },
  inviteLinkBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
  },
  inviteLinkText: {
    flex: 1,
    fontSize: 13,
    wordBreak: "break-all",
    fontFamily: "'Roboto Mono', 'Consolas', monospace",
    color: theme.palette.text.secondary,
    userSelect: "all",
  },
  settingsSection: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  settingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 0),
    gap: theme.spacing(2),
  },
  settingInfo: {
    flex: 1,
  },
  settingLabel: {
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  settingHint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
    lineHeight: 1.4,
  },
  chipSuperadmin: {
    backgroundColor: "#FF9800",
    color: "#fff",
    fontWeight: 700,
    fontSize: 11,
    height: 24,
    borderRadius: 6,
    "& .MuiChip-icon": {
      color: "#fff",
    },
  },
  chipAdmin: {
    backgroundColor: "#25D366",
    color: "#fff",
    fontWeight: 700,
    fontSize: 11,
    height: 24,
    borderRadius: 6,
    "& .MuiChip-icon": {
      color: "#fff",
    },
  },
  chipMember: {
    fontSize: 11,
    height: 24,
    borderRadius: 6,
    fontWeight: 500,
  },
  participantCount: {
    marginLeft: theme.spacing(1),
    fontWeight: 700,
    fontSize: 12,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  tableContainer: {
    maxHeight: 400,
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    "& .MuiTableHead-root .MuiTableCell-head": {
      backgroundColor: theme.palette.grey[100],
      fontWeight: 700,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      color: theme.palette.text.secondary,
    },
    "& .MuiTableRow-root:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  bulkButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    fontSize: 13,
  },
  actionButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    fontSize: 13,
  },
  loadingOverlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
  },
  removeCoverBtn: {
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: theme.palette.error.main + "14",
    },
  },
}));

const GroupEditForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { groupId } = useParams();
  const params = new URLSearchParams(location.search);
  const connectionId = params.get("connectionId");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [pictureUrl, setPictureUrl] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [removingSelected, setRemovingSelected] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const coverInputRef = useRef(null);

  const [inviteLink, setInviteLink] = useState("");
  const [loadingInvite, setLoadingInvite] = useState(false);

  const [announcement, setAnnouncement] = useState(false);
  const [locked, setLocked] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [promotingId, setPromotingId] = useState(null);

  const backUrl =
    "/group-management/groups/edit?" +
    (connectionId ? `connectionId=${connectionId}` : "");

  const goBack = () => history.push(backUrl);

  // ─── Data Fetching ───────────────────────────────────────────

  const fetchParticipants = useCallback(async () => {
    if (!connectionId || !groupId) return;
    setParticipantsLoading(true);
    try {
      const { data } = await api.get(
        `/group-management/groups/${encodeURIComponent(groupId)}/participants`,
        { params: { connectionId } }
      );
      const list = Array.isArray(data) ? data : [];
      // Backend retorna isAdmin/isSuperAdmin (bool), frontend usa admin (string)
      const mapped = list.map((p) => ({
        ...p,
        admin: p.isSuperAdmin ? "superadmin" : (p.isAdmin || p.admin === "admin") ? "admin" : null,
      }));
      setParticipants(mapped);
    } catch (err) {
      toastError(err);
    } finally {
      setParticipantsLoading(false);
    }
  }, [connectionId, groupId]);

  const fetchGroupInfo = useCallback(
    async (bustCache = false) => {
      if (!connectionId || !groupId) return;
      try {
        const { data } = await api.get(
          `/group-management/groups/${encodeURIComponent(groupId)}`,
          { params: { connectionId } }
        );
        setName(data.subject || "");
        setDescription(data.desc ?? data.description ?? "");
        let url = data.pictureUrl || "";
        if (url && bustCache) {
          url = `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
        }
        setPictureUrl(url);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [connectionId, groupId]
  );

  useEffect(() => {
    if (!connectionId || !groupId) {
      history.replace("/group-management");
      return;
    }
    setLoading(true);
    fetchGroupInfo();
  }, [connectionId, groupId, history, fetchGroupInfo]);

  useEffect(() => {
    if (!loading && connectionId && groupId) fetchParticipants();
  }, [loading, connectionId, groupId, fetchParticipants]);

  // ─── Cover Photo ─────────────────────────────────────────────

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    setCoverFile(file || null);
    setCoverPreview(file ? URL.createObjectURL(file) : null);
  };

  const clearCover = () => {
    setCoverFile(null);
    setCoverPreview(null);
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  // ─── Submit (Info Tab) ───────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!connectionId || !groupId) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("connectionId", connectionId);
      formData.append("name", name.trim());
      formData.append("description", description.trim());
      if (coverFile) formData.append("cover", coverFile);
      await api.patch(
        `/group-management/groups/${encodeURIComponent(groupId)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(i18n.t("groupManagement.groupUpdated"));
      history.push(backUrl);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // ─── Invite Code ─────────────────────────────────────────────

  const handleGetInviteCode = async () => {
    if (!connectionId || !groupId) return;
    setLoadingInvite(true);
    try {
      const { data } = await api.get(
        `/group-management/groups/${encodeURIComponent(groupId)}/invite-code`,
        { params: { connectionId } }
      );
      const link =
        data.link ||
        (data.code ? `https://chat.whatsapp.com/${data.code}` : "");
      setInviteLink(link);
      if (!link) toast.error(i18n.t("groupManagement.inviteLinkError"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingInvite(false);
    }
  };

  const handleCopyInviteLink = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink).then(() => {
      toast.success(i18n.t("groupManagement.inviteLinkCopied"));
    });
  };

  // ─── Settings ────────────────────────────────────────────────

  const handleToggleSetting = async (settingType) => {
    if (!connectionId || !groupId) return;
    setSavingSettings(true);
    try {
      let setting;
      if (settingType === "announcement") {
        setting = announcement ? "not_announcement" : "announcement";
      } else {
        setting = locked ? "unlocked" : "locked";
      }
      await api.put(
        `/group-management/groups/${encodeURIComponent(groupId)}/settings`,
        { connectionId, setting }
      );
      if (settingType === "announcement") setAnnouncement((prev) => !prev);
      else setLocked((prev) => !prev);
      toast.success(i18n.t("groupManagement.settingUpdated"));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingSettings(false);
    }
  };

  // ─── Participants ────────────────────────────────────────────

  const handleAddParticipant = async () => {
    const num = (newNumber || "").replace(/\D/g, "").trim();
    if (!num) {
      toast.error(i18n.t("groupManagement.participantNumberRequired"));
      return;
    }
    setAddingParticipant(true);
    try {
      await api.post(
        `/group-management/groups/${encodeURIComponent(groupId)}/participants`,
        { connectionId, participants: [num] }
      );
      toast.success(i18n.t("groupManagement.participantAdded"));
      setNewNumber("");
      fetchParticipants();
    } catch (err) {
      toastError(err);
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleRemoveParticipant = async (participant) => {
    setRemovingId(participant.id);
    try {
      await api.delete(
        `/group-management/groups/${encodeURIComponent(groupId)}/participants`,
        { data: { connectionId, participants: [participant.id] } }
      );
      toast.success(i18n.t("groupManagement.participantRemoved"));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(participant.id);
        return next;
      });
      fetchParticipants();
    } catch (err) {
      toastError(err);
    } finally {
      setRemovingId(null);
    }
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.size === participants.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(participants.map((p) => p.id)));
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRemoveSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setRemovingSelected(true);
    try {
      await api.delete(
        `/group-management/groups/${encodeURIComponent(groupId)}/participants`,
        { data: { connectionId, participants: ids } }
      );
      toast.success(
        i18n.t("groupManagement.participantsRemoved", { count: ids.length })
      );
      setSelectedIds(new Set());
      fetchParticipants();
    } catch (err) {
      toastError(err);
    } finally {
      setRemovingSelected(false);
    }
  };

  // ─── Promote / Demote ────────────────────────────────────────

  const handleToggleAdmin = async (participant) => {
    if (participant.admin === "superadmin") return;
    const isAdmin =
      participant.admin === "admin" || participant.admin === "superadmin";
    const action = isAdmin ? "demote" : "promote";
    setPromotingId(participant.id);
    try {
      await api.post(
        `/group-management/groups/${encodeURIComponent(groupId)}/participants/role`,
        { connectionId, participants: [participant.id], action }
      );
      toast.success(
        i18n.t(
          action === "promote"
            ? "groupManagement.promoted"
            : "groupManagement.demoted"
        )
      );
      fetchParticipants();
    } catch (err) {
      toastError(err);
    } finally {
      setPromotingId(null);
    }
  };

  const getRoleChip = (admin) => {
    if (admin === "superadmin") {
      return (
        <Chip
          size="small"
          label={i18n.t("groupManagement.superadmin")}
          className={classes.chipSuperadmin}
          icon={<StarIcon style={{ fontSize: 14 }} />}
        />
      );
    }
    if (admin === "admin") {
      return (
        <Chip
          size="small"
          label={i18n.t("groupManagement.admin")}
          className={classes.chipAdmin}
          icon={<StarIcon style={{ fontSize: 14 }} />}
        />
      );
    }
    return (
      <Chip
        size="small"
        label={i18n.t("groupManagement.member")}
        className={classes.chipMember}
        variant="outlined"
      />
    );
  };

  // ─── Guard ───────────────────────────────────────────────────

  if (!connectionId || !groupId) return null;

  // ─── Loading State ───────────────────────────────────────────

  if (loading) {
    return (
      <MainContainer>
        <MainHeader>
          <Button
            className={classes.backButton}
            onClick={goBack}
            startIcon={<ArrowBackIcon />}
          >
            {i18n.t("groupManagement.back")}
          </Button>
          <Title>{i18n.t("groupManagement.editGroup")}</Title>
        </MainHeader>
        <Box className={classes.loadingOverlay}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  // ─── Render ──────────────────────────────────────────────────

  return (
    <MainContainer>
      <MainHeader>
        <Button
          className={classes.backButton}
          onClick={goBack}
          startIcon={<ArrowBackIcon />}
        >
          {i18n.t("groupManagement.back")}
        </Button>
        <Title>{i18n.t("groupManagement.editGroup")}</Title>
      </MainHeader>

      <Box className={classes.root}>
        <Paper className={classes.paper}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            className={classes.tabs}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab
              label={i18n.t("groupManagement.tabGroupInfo")}
              icon={<InfoIcon />}
            />
            <Tab
              label={i18n.t("groupManagement.participants")}
              icon={<PeopleIcon />}
            />
            <Tab
              label={i18n.t("groupManagement.groupSettings")}
              icon={<SettingsIcon />}
            />
          </Tabs>

          {/* ─── Tab 0: Info ─────────────────────────────────── */}
          {tabValue === 0 && (
            <Fade in timeout={300}>
              <div className={classes.tabPanel} role="tabpanel">
                <form onSubmit={handleSubmit}>
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("groupManagement.groupName")}
                  </Typography>
                  <TextField
                    className={classes.field}
                    fullWidth
                    variant="outlined"
                    label={i18n.t("groupManagement.groupName")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={i18n.t(
                      "groupManagement.groupNamePlaceholder"
                    )}
                    size="small"
                  />

                  <Typography className={classes.sectionTitle}>
                    {i18n.t("groupManagement.groupDescription")}
                  </Typography>
                  <TextField
                    className={classes.field}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    label={i18n.t("groupManagement.groupDescription")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={i18n.t(
                      "groupManagement.groupDescriptionPlaceholder"
                    )}
                  />

                  <Typography className={classes.sectionTitle}>
                    <PhotoCameraIcon className={classes.sectionTitleIcon} />
                    {i18n.t("groupManagement.groupCoverPhoto")}
                  </Typography>
                  <Box className={classes.coverSection}>
                    {coverPreview || coverFile ? (
                      <Box className={classes.coverActions}>
                        {coverPreview && (
                          <img
                            src={coverPreview}
                            alt=""
                            className={classes.coverPreview}
                          />
                        )}
                        <Tooltip
                          title={i18n.t("groupManagement.removeCover")}
                        >
                          <IconButton
                            size="small"
                            onClick={clearCover}
                            className={classes.removeCoverBtn}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : pictureUrl ? (
                      <Box>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          style={{ marginBottom: 4 }}
                        >
                          {i18n.t("groupManagement.currentCover")}
                        </Typography>
                        <img
                          src={pictureUrl}
                          alt=""
                          className={classes.coverCurrent}
                        />
                      </Box>
                    ) : null}
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/*"
                      className={classes.inputFile}
                      id="edit-group-cover"
                      onChange={handleCoverChange}
                    />
                    <label htmlFor="edit-group-cover" style={{ width: "100%" }}>
                      <Box
                        className={classes.coverPlaceholder}
                        component="span"
                      >
                        <PhotoCameraIcon
                          style={{
                            fontSize: 36,
                            color: "rgba(0,0,0,0.3)",
                            marginBottom: 8,
                          }}
                        />
                        <Typography variant="body2" color="textSecondary">
                          {coverFile
                            ? coverFile.name
                            : pictureUrl
                            ? i18n.t("groupManagement.changeCoverPhoto")
                            : i18n.t("groupManagement.addCoverPhoto")}
                        </Typography>
                      </Box>
                    </label>
                  </Box>

                  <Button
                    type="submit"
                    className={classes.submitButton}
                    variant="contained"
                    color="primary"
                    disabled={saving}
                    startIcon={
                      saving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                  >
                    {i18n.t("groupManagement.saveGroup")}
                  </Button>
                </form>

                {/* ─── Invite Link ──────────────────────────────── */}
                <Divider style={{ margin: "28px 0 20px" }} />
                <Typography className={classes.sectionTitle}>
                  <LinkIcon className={classes.sectionTitleIcon} />
                  {i18n.t("groupManagement.inviteLink")}
                </Typography>
                {inviteLink ? (
                  <Box className={classes.inviteLinkBox}>
                    <Typography className={classes.inviteLinkText}>
                      {inviteLink}
                    </Typography>
                    <Tooltip
                      title={i18n.t("groupManagement.copyInviteLink")}
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyInviteLink}
                        color="primary"
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    className={classes.actionButton}
                    startIcon={
                      loadingInvite ? (
                        <CircularProgress size={16} />
                      ) : (
                        <LinkIcon />
                      )
                    }
                    onClick={handleGetInviteCode}
                    disabled={loadingInvite}
                    style={{ marginBottom: 8 }}
                  >
                    {i18n.t("groupManagement.generateInviteLink")}
                  </Button>
                )}
              </div>
            </Fade>
          )}

          {/* ─── Tab 1: Participants ───────────────────────────── */}
          {tabValue === 1 && (
            <Fade in timeout={300}>
              <div className={classes.tabPanel} role="tabpanel">
                <Typography className={classes.sectionTitle}>
                  <PeopleIcon className={classes.sectionTitleIcon} />
                  {i18n.t("groupManagement.participants")}
                  {participants.length > 0 && (
                    <Chip
                      size="small"
                      label={participants.length}
                      className={classes.participantCount}
                    />
                  )}
                </Typography>

                <Box className={classes.addParticipantRow}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder={i18n.t("groupManagement.participantNumber")}
                    value={newNumber}
                    onChange={(e) => setNewNumber(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddParticipant();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    className={classes.addInput}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.actionButton}
                    startIcon={
                      addingParticipant ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PersonAddIcon />
                      )
                    }
                    onClick={handleAddParticipant}
                    disabled={addingParticipant || !newNumber.trim()}
                    style={{ borderRadius: 8 }}
                  >
                    {i18n.t("groupManagement.addParticipant")}
                  </Button>
                  {participants.length > 0 && selectedIds.size > 0 && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      className={classes.bulkButton}
                      startIcon={
                        removingSelected ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <DeleteOutlineIcon />
                        )
                      }
                      onClick={handleRemoveSelected}
                      disabled={removingSelected || selectedIds.size === 0}
                    >
                      {i18n.t("groupManagement.removeSelected")}
                      {` (${selectedIds.size})`}
                    </Button>
                  )}
                </Box>

                {participantsLoading ? (
                  <Box className={classes.loadingOverlay}>
                    <CircularProgress size={28} />
                  </Box>
                ) : participants.length === 0 ? (
                  <Box className={classes.emptyState}>
                    <PeopleIcon
                      style={{ fontSize: 48, opacity: 0.3, marginBottom: 8 }}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("groupManagement.noParticipants")}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    className={classes.tableContainer}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={
                                selectedIds.size > 0 &&
                                selectedIds.size < participants.length
                              }
                              checked={
                                participants.length > 0 &&
                                selectedIds.size === participants.length
                              }
                              onChange={handleToggleSelectAll}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            {i18n.t("groupManagement.participantName")}
                          </TableCell>
                          <TableCell>
                            {i18n.t("groupManagement.participantNumber")}
                          </TableCell>
                          <TableCell align="center">
                            {i18n.t("groupManagement.role")}
                          </TableCell>
                          <TableCell align="right">
                            {i18n.t("groupManagement.actions")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {participants.map((p) => {
                          const isAdmin =
                            p.admin === "admin" || p.admin === "superadmin";
                          const isSuperadmin = p.admin === "superadmin";
                          return (
                            <TableRow key={p.id} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedIds.has(p.id)}
                                  onChange={() => handleToggleSelect(p.id)}
                                  color="primary"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 500 }}
                                >
                                  {p.name || "-"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {p.number || p.id}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {getRoleChip(p.admin)}
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  display="inline-flex"
                                  alignItems="center"
                                  style={{ gap: 4 }}
                                >
                                  {!isSuperadmin && (
                                    <Tooltip
                                      title={i18n.t(
                                        isAdmin
                                          ? "groupManagement.demote"
                                          : "groupManagement.promote"
                                      )}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() => handleToggleAdmin(p)}
                                        disabled={promotingId === p.id}
                                      >
                                        {promotingId === p.id ? (
                                          <CircularProgress size={16} />
                                        ) : isAdmin ? (
                                          <StarIcon
                                            fontSize="small"
                                            style={{ color: "#25D366" }}
                                          />
                                        ) : (
                                          <StarBorderIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {!isSuperadmin && (
                                    <Tooltip
                                      title={i18n.t(
                                        "groupManagement.removeParticipant"
                                      )}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleRemoveParticipant(p)
                                        }
                                        disabled={removingId === p.id}
                                      >
                                        {removingId === p.id ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <DeleteOutlineIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </div>
            </Fade>
          )}

          {/* ─── Tab 2: Settings ───────────────────────────────── */}
          {tabValue === 2 && (
            <Fade in timeout={300}>
              <div className={classes.tabPanel} role="tabpanel">
                <Typography className={classes.sectionTitle}>
                  <SettingsIcon className={classes.sectionTitleIcon} />
                  {i18n.t("groupManagement.groupSettings")}
                </Typography>

                <Box className={classes.settingsSection}>
                  <Box className={classes.settingRow}>
                    <Box className={classes.settingInfo}>
                      <Typography className={classes.settingLabel}>
                        {i18n.t("groupManagement.settingAnnouncement")}
                      </Typography>
                      <Typography className={classes.settingHint}>
                        {i18n.t("groupManagement.settingAnnouncementHint")}
                      </Typography>
                    </Box>
                    <Switch
                      checked={announcement}
                      onChange={() => handleToggleSetting("announcement")}
                      disabled={savingSettings}
                      color="primary"
                    />
                  </Box>

                  <Divider />

                  <Box className={classes.settingRow}>
                    <Box className={classes.settingInfo}>
                      <Typography className={classes.settingLabel}>
                        {i18n.t("groupManagement.settingLocked")}
                      </Typography>
                      <Typography className={classes.settingHint}>
                        {i18n.t("groupManagement.settingLockedHint")}
                      </Typography>
                    </Box>
                    <Switch
                      checked={locked}
                      onChange={() => handleToggleSetting("locked")}
                      disabled={savingSettings}
                      color="primary"
                    />
                  </Box>
                </Box>

                {savingSettings && (
                  <Box display="flex" justifyContent="center" py={1}>
                    <CircularProgress size={22} />
                  </Box>
                )}
              </div>
            </Fade>
          )}
        </Paper>
      </Box>
    </MainContainer>
  );
};

export default GroupEditForm;
